import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Bookmark, BookmarkCheck, BookOpen, Check, Download, ExternalLink, FileText, ShoppingCart, Sparkles, Star, Target, Volume2, X } from 'lucide-react';
import { toast } from 'sonner';
import { BookCover } from './BookCover';
import { BookCarousel } from './BookCarousel';
import { LearnWithOlliePanel } from '@/components/learn/LearnWithOlliePanel';
import { QuizCtaPanel } from '@/components/quiz/QuizCtaPanel';
import { PaywalledSection } from '@/components/paywall/PaywalledSection';
import { CosmicTypingCta } from '@/components/games/cosmic-typer/CosmicTypingCta';
import { CosmicTyperOverlay } from '@/components/games/cosmic-typer/CosmicTyperOverlay';
import { GlobalLeaderboardCard } from '@/components/games/cosmic-typer/GlobalLeaderboardCard';
import { extractBookWords } from '@/components/games/cosmic-typer/extractBookWords';
import { BookTrackerPanel } from '@/components/reading-tracker/BookTrackerPanel';
import { BookReviewsPanel } from '@/components/books/BookReviewsPanel';
import { DescriptionAudioButton } from '@/components/books/DescriptionAudioButton';
import { ParentPinDialog } from '@/components/account/ParentPinDialog';
import { AverageRatingBadge } from '@/components/ui/AverageRatingBadge';
import { Button } from '@/components/ui/Button';
import { bookActivitiesService } from '@/services/book-activities.service';
import { booksService } from '@/services/books.service';
import { bookmarksService } from '@/services/bookmarks.service';
import { gamesService } from '@/services/games.service';
import { readingTrackerService } from '@/services/reading-tracker.service';
import { ApiError } from '@/services/http';
import { useReadersStore } from '@/store/readers.store';
import { useReadingTrackerStore } from '@/store/reading-tracker.store';
import { useIsGuest } from '@/hooks/useIsGuest';
import { useAccountGate } from '@/hooks/useAccountGate';
import { GuestGateCard } from '@/components/account/GuestGateCard';
import { cn } from '@/lib/cn';
import type { BookActivityView, BookLabelView, BookView, SimilarBookView } from '@/types/api';

/**
 * BookDetailModal — right-slide-in sheet that holds the full book detail
 * view per docs/LIBRARY.md. Opens when a BookCard is clicked.
 *
 * Layout (Phase 5+):
 *   - Modal header: close button.
 *   - Segmented "Book Details / Book Resources / Games" toggle pinned just
 *     below the header.
 *   - Active tab content scrolls in the body:
 *       Details   → cover, bookmark, title/author, label lines, reading
 *                   + interest levels, description, similar books.
 *       Resources → vocabulary bookmark, activities, Learn With Ollie, Quiz CTA.
 *       Games     → Cosmic Typing CTA and Global Leaderboard.
 *
 * Responsibilities:
 *   - Render cover, title, author, description, and all label groups.
 *   - Toggle bookmarks for the currently-selected reader (hidden when
 *     no reader is picked — parents can still browse the catalogue).
 *   - Lazy-fetch up to 5 similar books once the modal is open; clicking
 *     one swaps the modal's target book without a full unmount (so the
 *     sheet stays open and the transition feels continuous). Tab state
 *     resets to "Details" on book change so the cover always lands on
 *     screen, not whatever tab the previous book left open.
 */
export interface BookDetailModalProps {
  book: BookView | null;
  onOpenChange: (open: boolean) => void;
  /**
   * Fires when the caller should swap the modal's target book — used when
   * the user clicks a similar book. The parent Library page passes the
   * same `setSelectedBook` handler it uses for BookCards.
   */
  onSelectBook?: (book: BookView) => void;
  /**
   * When `true`, the modal opens with the in-modal reading tracker already
   * engaged instead of the Details tab. Used by the Bookshelf badge so a
   * tap on the progress chip lands the reader directly on the tracker.
   * Defaults to `false`.
   */
  initialTrackerOpen?: boolean;
}

export function BookDetailModal({
  book,
  onOpenChange,
  onSelectBook,
  initialTrackerOpen = false,
}: BookDetailModalProps) {
  const open = book !== null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-or-slate-900/55 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out" />
        <Dialog.Content
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-or-pop focus:outline-none',
            'sm:max-w-[720px]',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-right',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right',
          )}
          aria-describedby={undefined}
        >
          {book ? (
            <ModalBody
              book={book}
              onOpenChange={onOpenChange}
              onSelectBook={onSelectBook}
              initialTrackerOpen={initialTrackerOpen}
            />
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

type ModalTab = 'details' | 'resources' | 'games';

function ModalBody({
  book,
  onOpenChange,
  onSelectBook,
  initialTrackerOpen,
}: {
  book: BookView;
  onOpenChange: (open: boolean) => void;
  onSelectBook?: (book: BookView) => void;
  initialTrackerOpen: boolean;
}) {
  const selectedReader = useReadersStore((s) => s.selectedReader());
  const isGuest = useIsGuest();
  const { openAccountGate } = useAccountGate();

  // Reading-tracker entry point. The full tracker now lives inside the
  // slideout — clicking the CTA flips `trackerOpen` to swap the body
  // contents, no route navigation. The previous behaviour navigated to
  // `/reader/:readerId/book/:bookId/tracker`; that page still exists
  // for any deep links but the modal is the primary surface.
  const [trackerOpen, setTrackerOpen] = useState(initialTrackerOpen);

  // Subscribe to the reading-tracker store so the CTA label stays in
  // sync with mutations made inside the panel (create / abandon /
  // mark-day). Keys mirror the store's `keyFor` convention.
  const trackerKeyFor = useReadingTrackerStore((s) => s.keyFor);
  const setTrackerGoal = useReadingTrackerStore((s) => s.setGoal);
  const trackerKey = selectedReader ? trackerKeyFor(selectedReader.id, book.id) : null;
  const cachedGoal = useReadingTrackerStore((s) =>
    trackerKey ? s.goalsByBook[trackerKey] : undefined,
  );
  // `cachedGoal` is undefined while the active-goal probe is in flight,
  // null once we've confirmed there's no active goal, or a `ReadingGoalView`
  // for an active/completed plan. The CTA label has three states:
  //   - no goal           → "Start reading plan"
  //   - in-progress goal  → "Update Reading Progress"
  //   - completed goal    → "Reading Plan Completed"
  // The button stays clickable even on completed so the user can re-enter
  // the panel to review the celebration banner or hit "Start Over".
  // 'abandoned' shouldn't surface here — `Start Over` clears the cache to
  // null — but if it ever did, treat it as "no goal".
  const cachedGoalStatus =
    cachedGoal && cachedGoal.status !== 'abandoned' ? cachedGoal.status : null;
  const ctaLabel =
    cachedGoalStatus === 'completed'
      ? 'Reading Plan Completed'
      : cachedGoalStatus === 'active'
        ? 'Update Reading Progress'
        : 'Start reading plan';

  const startReadingPlan = () => {
    if (!selectedReader) return;
    setTrackerOpen(true);
  };

  const [bookmarkId, setBookmarkId] = useState<string | null>(null);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const [bookmarkPending, setBookmarkPending] = useState(false);

  const [similar, setSimilar] = useState<SimilarBookView[]>([]);
  const [similarLoading, setSimilarLoading] = useState(true);

  // Activities live on a separate endpoint; we lazy-fetch when the modal
  // opens (or swaps to a new book) so the Details tab doesn't pay the
  // round-trip when the user never opens the Resources tab.
  const [activities, setActivities] = useState<BookActivityView[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);

  // Tab state. Reset to 'details' whenever the modal swaps to a different
  // book (e.g. clicking a similar-book inside the modal) so the new book's
  // cover always lands on screen rather than the previous book's Resources
  // tab. The scroll position is also reset on tab change so a long Details
  // scroll doesn't leave Resources mid-page when toggled.
  const [tab, setTab] = useState<ModalTab>('details');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    setTab('details');
    // Also reset the in-modal tracker takeover when swapping books — the
    // panel is per-book so it shouldn't survive a similar-book click.
    setTrackerOpen(false);
  }, [book.id]);

  // Cosmic Typing game state — owns its own boolean rather than a route so
  // closing the game leaves the modal exactly as the user left it. Words
  // are derived from the description; memoised so CosmicTyper doesn't
  // re-shuffle the queue on every render.
  const [gameOpen, setGameOpen] = useState(false);
  const cosmicWords = useMemo(() => extractBookWords(book.description, 30), [book.description]);
  // If the book swaps mid-game (similar-book click), close the overlay so
  // the new book's word list isn't fed into a run that started on a
  // different book.
  useEffect(() => {
    setGameOpen(false);
  }, [book.id]);

  // Bumping `leaderboardKey` forces the GlobalLeaderboardCard to re-fetch.
  // We bump it after every successful score submission so the widget
  // shows the new high score without a full modal remount.
  const [leaderboardKey, setLeaderboardKey] = useState(0);

  // Local override of the book's aggregate rating. Defaults to whatever
  // the book payload already carried; the BookReviewsPanel notifies
  // us through `onAggregateChanged` after a write so the hero badge
  // can repaint instantly without a /books refetch. Reset whenever
  // the modal swaps to a different book.
  const [ratingOverride, setRatingOverride] = useState<{
    averageRating: number | null;
    ratingCount: number;
  } | null>(null);
  useEffect(() => {
    setRatingOverride(null);
  }, [book.id]);
  const displayAverage = ratingOverride?.averageRating ?? book.averageRating;
  const displayRatingCount = ratingOverride?.ratingCount ?? book.ratingCount;

  // Parent-PIN gate for the "Buy on Amazon" link. Clicking the link
  // opens the dialog; the actual `window.open` fires inside the dialog's
  // `onVerified` callback, which runs synchronously after the verify
  // fetch resolves (i.e. still inside the user-gesture window of the
  // dialog's submit click). No placeholder tab — opening one
  // pre-verification surfaced as a visible blank tab to the user.
  const [amazonPinOpen, setAmazonPinOpen] = useState(false);
  const openAmazonAfterPin = () => {
    if (!book.amazonAffiliateUrl) return;
    window.open(book.amazonAffiliateUrl, '_blank', 'noopener,noreferrer');
  };
  const handleAmazonClick = () => {
    if (!book.amazonAffiliateUrl) return;
    if (isGuest) {
      openAccountGate('amazon');
      return;
    }
    setAmazonPinOpen(true);
  };

  // --- Bookmark probe: does this reader already have this book saved? ---
  useEffect(() => {
    if (!selectedReader) {
      setBookmarkId(null);
      return;
    }
    let cancelled = false;
    setBookmarkLoading(true);

    (async () => {
      try {
        const result = await bookmarksService.check({
          readerId: selectedReader.id,
          bookId: book.id,
        });
        if (cancelled) return;
        setBookmarkId(result.bookmarked ? (result.bookmark?.id ?? null) : null);
      } catch (err) {
        if (cancelled) return;
        // A failed probe just means we render an inert "Add to list" state;
        // no need to shout at the user.
        console.warn('Bookmark check failed:', err);
        setBookmarkId(null);
      } finally {
        if (!cancelled) setBookmarkLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [book.id, selectedReader]);

  // --- Active reading goal: drives the CTA label inside the modal. ---
  // Re-probed on every (reader, book) change. Mirrors the contract on
  // ReadingTrackerPage — we cache the result in the shared zustand store
  // so the panel skips its own probe on open.
  useEffect(() => {
    if (!selectedReader || !trackerKey) return;
    let cancelled = false;
    (async () => {
      try {
        const active = await readingTrackerService.active(selectedReader.id, book.id);
        if (cancelled) return;
        setTrackerGoal(trackerKey, active);
      } catch (err) {
        if (cancelled) return;
        // Failed probes leave the cache as-is. Worst case the CTA reads
        // "Start reading plan" until the user opens the panel, where the
        // panel's own bootstrap retry picks up the slack.
        console.warn('Active reading goal probe failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [book.id, selectedReader?.id]);

  // --- Similar books: cosine neighbours, up to 5. ---
  useEffect(() => {
    let cancelled = false;
    setSimilarLoading(true);

    (async () => {
      try {
        const result = await booksService.similar(book.id, { limit: 5 });
        if (!cancelled) setSimilar(result.books);
      } catch (err) {
        if (cancelled) return;
        // Log but don't toast — the section just renders empty.
        console.warn('Similar books failed:', err);
        setSimilar([]);
      } finally {
        if (!cancelled) setSimilarLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [book.id]);

  // --- Activities: per-book downloadable PDFs (Resources tab). ---
  // Guests never reach the Resources content (it's gated), and the endpoint
  // is auth-only — so skip the fetch entirely to avoid a 401.
  useEffect(() => {
    if (isGuest) {
      setActivities([]);
      setActivitiesLoading(false);
      return;
    }
    let cancelled = false;
    setActivitiesLoading(true);

    (async () => {
      try {
        const rows = await bookActivitiesService.list(book.id);
        if (!cancelled) setActivities(rows);
      } catch (err) {
        if (cancelled) return;
        // Same posture as similar books — log and render empty rather
        // than yelling at the reader.
        console.warn('Activities load failed:', err);
        setActivities([]);
      } finally {
        if (!cancelled) setActivitiesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [book.id, isGuest]);

  const toggleBookmark = async () => {
    if (!selectedReader || bookmarkPending) return;
    setBookmarkPending(true);

    try {
      if (bookmarkId) {
        await bookmarksService.remove(bookmarkId);
        setBookmarkId(null);
        toast.success(`Removed from ${selectedReader.name}'s list.`);
      } else {
        const created = await bookmarksService.create({
          readerId: selectedReader.id,
          bookId: book.id,
        });
        setBookmarkId(created.id);
        toast.success(`Saved to ${selectedReader.name}'s list.`);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update bookmark.');
    } finally {
      setBookmarkPending(false);
    }
  };

  const handleSelectSimilar = (next: BookView) => {
    // Delegate to the parent so the Library's modal state stays the source
    // of truth — a simple setState here would leave the parent's selection
    // pointing at the original book.
    if (onSelectBook) onSelectBook(next);
  };

  // Centralised tab switcher: updates state and snaps the scroll back to
  // the top so the new tab's first section is visible. Without this, a
  // user mid-scroll on Details would land somewhere arbitrary inside
  // Resources (often below the fold).
  const switchTab = (next: ModalTab) => {
    if (next === tab) return;
    setTab(next);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  return (
    <div className="flex h-full flex-col">
      {/* Radix requires a Dialog.Title for screen readers. The visible
          surface uses the tab toggle below, so the title is hidden via
          `sr-only` but still announces the current book on open. */}
      <Dialog.Title className="sr-only">{book.title}</Dialog.Title>

      <header className="flex items-center justify-end border-b border-or-slate-100 px-6 py-3">
        <Dialog.Close
          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-or-slate-500 transition hover:bg-or-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
          aria-label="Close"
          onClick={() => onOpenChange(false)}
        >
          <X className="h-4 w-4" aria-hidden />
        </Dialog.Close>
      </header>

      {/* Segmented pill toggle — pinned outside the scrollable area so it
          stays visible while either tab's content scrolls. Hidden when
          the in-modal reading tracker takes over the body. */}
      {!trackerOpen ? (
        <div className="border-b border-or-slate-100 px-6 pb-4 pt-2">
          <TabToggle tab={tab} onChange={switchTab} />
        </div>
      ) : null}

      {trackerOpen && selectedReader ? (
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <BookTrackerPanel
            book={book}
            readerId={selectedReader.id}
            onBackToBook={() => setTrackerOpen(false)}
          />
        </div>
      ) : (
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-6 py-6"
        role="tabpanel"
        id={`book-tabpanel-${tab}`}
        aria-labelledby={`book-tab-${tab}`}
      >
        {tab === 'details' ? (
          <>
            {/* --- Hero row: cover + core metadata --- */}
            <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-6">
              <div className="mx-auto shrink-0 sm:mx-0">
                <BookCover
                  title={book.title}
                  author={book.author}
                  src={book.bookCover}
                  seed={book.id}
                  w={160}
                  h={230}
                />
              </div>
              <div className="flex flex-1 flex-col gap-3">
                {isGuest ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="primary" size="md" onClick={() => openAccountGate('save')}>
                      <Bookmark className="h-4 w-4" aria-hidden />
                      Save &amp; start a reading plan
                    </Button>
                  </div>
                ) : selectedReader ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant={cachedGoalStatus === 'completed' ? 'success' : 'primary'}
                      size="md"
                      onClick={startReadingPlan}
                    >
                      {cachedGoalStatus === 'completed' ? (
                        <Check className="h-4 w-4" aria-hidden />
                      ) : (
                        <Target className="h-4 w-4" aria-hidden />
                      )}
                      {ctaLabel}
                    </Button>
                    <Button
                      variant={bookmarkId ? 'secondary' : 'secondary'}
                      size="md"
                      onClick={toggleBookmark}
                      disabled={bookmarkLoading || bookmarkPending}
                    >
                      {bookmarkId ? (
                        <>
                          <BookmarkCheck className="h-4 w-4" aria-hidden />
                          Remove from list
                        </>
                      ) : (
                        <>
                          <Bookmark className="h-4 w-4" aria-hidden />
                          {bookmarkPending ? 'Saving…' : 'Add to bookshelf'}
                        </>
                      )}
                    </Button>
                  </div>
                ) : null}
                <div>
                  <h2 className="font-display text-[24px] font-bold leading-tight text-or-slate-900">
                    {book.title}
                  </h2>
                  {book.author ? (
                    <div className="mt-1 text-[13px] text-or-slate-600">by {book.author}</div>
                  ) : null}
                  {book.amazonAffiliateUrl ? (
                    <button
                      type="button"
                      onClick={handleAmazonClick}
                      className="mt-1 inline-flex items-center gap-1 text-[12px] text-or-blue-600 underline underline-offset-2 transition hover:text-or-blue-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-blue-400/40"
                      aria-label={`Buy ${book.title} on Amazon (parent PIN required)`}
                    >
                      <ShoppingCart className="h-3 w-3" aria-hidden />
                      Buy on Amazon
                    </button>
                  ) : null}
                  {displayAverage !== null && displayRatingCount > 0 ? (
                    <div className="mt-2">
                      <AverageRatingBadge
                        averageRating={displayAverage}
                        ratingCount={displayRatingCount}
                      />
                    </div>
                  ) : null}
                </div>
                <LabelLine label="Genres" labels={book.genres} />
                <LabelLine label="Topics" labels={book.topics} />
                <LabelLine label="Time periods" labels={book.timePeriods} />
                <LabelLine label="Historical events" labels={book.historicalEvents} />
              </div>
            </div>

            {/* --- Reading levels + description --- */}
            <div className="mt-8 flex flex-col gap-5 rounded-2xl border border-or-slate-100 bg-or-cream-50/60 p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <LabelChipGroup label="Reading levels" labels={book.readingLevels} />
                <LabelChipGroup label="Interest levels" labels={book.interestLevels} />
              </div>
              {book.description ? (
                <div>
                  <div className="mb-1.5 flex items-center justify-between gap-3">
                    <div className="or-kicker">Ollie's Book Summary</div>
                    {isGuest ? (
                      <button
                        type="button"
                        onClick={() => openAccountGate('read-aloud')}
                        className="inline-flex items-center gap-1.5 rounded-full border border-or-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-or-slate-700 transition hover:bg-or-slate-50"
                      >
                        <Volume2 className="h-3.5 w-3.5" aria-hidden />
                        Listen
                      </button>
                    ) : (
                      <DescriptionAudioButton bookId={book.id} />
                    )}
                  </div>
                  <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-or-slate-700">
                    {book.description}
                  </p>
                </div>
              ) : null}
            </div>

            {/* --- Similar books --- */}
            <div className="mt-10">
              <BookCarousel
                mode="sync"
                title="Similar books"
                kicker="You might also like"
                books={similar}
                loading={similarLoading}
                onSelectBook={handleSelectSimilar}
              />
            </div>

            {/* --- Reader reviews + star ratings --- */}
            {isGuest ? (
              <div className="mt-10">
                <GuestGateCard
                  icon={Star}
                  title="Reader reviews"
                  subtitle="Create a free account to read and write reviews."
                  onActivate={() => openAccountGate('reviews')}
                />
              </div>
            ) : (
              <BookReviewsPanel bookId={book.id} onAggregateChanged={setRatingOverride} />
            )}
          </>
        ) : tab === 'resources' ? (
          isGuest ? (
            <GuestGateCard
              icon={BookOpen}
              title="Quizzes, activities &amp; more"
              subtitle="Create a free account to unlock quizzes, printable activities, vocabulary bookmarks, and Ask Ollie."
              onActivate={() => openAccountGate('resources')}
            />
          ) : (
          <>
            {/* --- Vocabulary bookmark: admin-uploaded printable resource ---
                Hidden entirely when no bookmark has been uploaded for this
                book. When present, wrapped in PaywalledSection so free-tier
                readers see the card but every interaction (Open, Download)
                routes to the paywall instead of leaking the asset URL. */}
            {book.vocabularyBookmark ? (
              <PaywalledSection
                feature="vocabulary-bookmark"
                ctaLabel="Unlock vocabulary bookmark"
              >
                <VocabularyBookmarkCard
                  url={book.vocabularyBookmark}
                  bookTitle={book.title}
                />
              </PaywalledSection>
            ) : null}

            {/* --- Activities: per-book downloadable PDFs ---
                Lazy-fetched above. Hidden entirely when the book has
                none and we're past the loading state, so books without
                worksheets don't show an empty section. Free readers see
                the card but every Open/Download click routes to the
                paywall via PaywalledSection. */}
            {activitiesLoading ? (
              <ActivitiesCardSkeleton />
            ) : activities.length > 0 ? (
              <PaywalledSection feature="activity-pdf" ctaLabel="Unlock activities">
                <ActivitiesCard activities={activities} bookTitle={book.title} />
              </PaywalledSection>
            ) : null}

            {/* --- Learn With Ollie: admin-curated prompts ---
                Wrapped in PaywalledSection so free-tier readers see the panel
                but every interaction routes to the paywall instead of
                burning an OpenAI call. */}
            <PaywalledSection feature="learn-with-ollie" ctaLabel="Unlock Ask Ollie">
              <LearnWithOlliePanel bookId={book.id} />
            </PaywalledSection>

            {/* --- Quiz CTA (self-gates when no live quiz) --- */}
            <QuizCtaPanel parentKind="book" parentKey={book.id} />
          </>
          )
        ) : (
          <>
            {/* --- Games tab: Cosmic Typing CTA + Global Leaderboard ---
                Hidden when the description is too thin to produce a
                meaningful word list — there's no point launching the game
                with five words. */}
            {cosmicWords.length >= 8 ? (
              <>
                <CosmicTypingCta
                  wordCount={cosmicWords.length}
                  onLaunch={() => setGameOpen(true)}
                />
                <GlobalLeaderboardCard
                  bookId={book.id}
                  readerId={selectedReader?.id}
                  readerName={selectedReader?.name}
                  readerAvatarSlug={selectedReader?.avatarSlug}
                  refreshKey={leaderboardKey}
                />
              </>
            ) : (
              <div className="rounded-2xl border border-or-slate-100 bg-or-cream-50/60 p-5 text-[13px] text-or-slate-600">
                No games available for this book yet.
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* Cosmic Typing overlay. Rendered outside the tab panel so it
          covers the whole viewport regardless of which tab is active or
          how far the panel is scrolled.

          onGameOver fires from inside the game when a run ends. We POST
          the score to the leaderboard endpoint with the surrounding book
          context. Submission is gated on having a selected reader — a
          parent browsing the catalogue without a kid profile picked
          can still play, we just don't have a reader_id to attribute
          the score to. The submit call is fire-and-forget; failures
          surface as a quiet toast so a flaky network doesn't trample
          the game's own end-of-run screen. */}
      <CosmicTyperOverlay
        open={gameOpen}
        words={cosmicWords}
        onClose={() => setGameOpen(false)}
        onGameOver={(result) => {
          if (!selectedReader) {
            // No reader picked — game ran for fun, but there's nobody
            // to attribute the score to. Silently skip submission.
            return;
          }
          gamesService
            .submit({
              readerId: selectedReader.id,
              bookId: book.id,
              score: result.score,
              gameSlug: 'cosmic_typer',
              stats: {
                level: result.level,
                bestCombo: result.bestCombo,
                wpm: result.wpm,
                accuracy: result.accuracy,
                wordsTyped: result.wordsTyped,
                grade: result.grade,
              },
            })
            .then((res) => {
              if (res.improved) {
                toast.success(`New high score for ${selectedReader.name}: ${res.bestScore}!`);
              }
              // Always re-fetch the leaderboard summary — even a non-PB
              // run bumps `play_count`, and may have changed the global
              // standings if someone else's row updated in parallel.
              setLeaderboardKey((k) => k + 1);
            })
            .catch((err) => {
              console.warn('Game score submit failed:', err);
              toast.error('Could not save your score. The game still counted!');
            });
        }}
      />

      {/* Parent-PIN gate for the affiliate link. Only mounted when the
          book actually has an affiliate URL — keeps the dialog tree
          empty for the common case (no link, no gate). */}
      {book.amazonAffiliateUrl ? (
        <ParentPinDialog
          open={amazonPinOpen}
          onOpenChange={setAmazonPinOpen}
          title={`Open the Amazon page for "${book.title}"?`}
          description="Enter the 4-digit parent PIN to open the buy link in a new tab."
          onVerified={openAmazonAfterPin}
        />
      ) : null}
    </div>
  );
}

/**
 * Three-position segmented pill that drives the Details / Resources / Games tabs
 * inside BookDetailModal. Wraps the two halves in a single rounded
 * container with the active half filled in the brand orange — feels
 * like a kid-mode on/off switch and matches the page's existing
 * "rounded-full + or-orange" affordances.
 *
 * Implements the `tablist` ARIA pattern so screen readers announce the
 * pair as a single tab group; the visible selection state is also
 * propagated via `aria-selected` so assistive tech doesn't rely on
 * colour alone.
 */
function TabToggle({ tab, onChange }: { tab: ModalTab; onChange: (next: ModalTab) => void }) {
  return (
    <div
      role="tablist"
      aria-label="Book sections"
      className="inline-flex w-full items-center rounded-full border border-or-orange-200 bg-or-cream-50 p-1"
    >
      <TabButton
        id="book-tab-details"
        controls="book-tabpanel-details"
        active={tab === 'details'}
        onClick={() => onChange('details')}
      >
        Book Details
      </TabButton>
      <TabButton
        id="book-tab-resources"
        controls="book-tabpanel-resources"
        active={tab === 'resources'}
        onClick={() => onChange('resources')}
      >
        Book Resources
      </TabButton>
      {/* Games tab hidden for now — re-enable to restore Cosmic Typing.
          The games content branch in the body is left intact but unreachable
          while this button is absent (it's the `else` fallback panel). */}
      {/* <TabButton
        id="book-tab-games"
        controls="book-tabpanel-games"
        active={tab === 'games'}
        onClick={() => onChange('games')}
      >
        Games
      </TabButton> */}
    </div>
  );
}

function TabButton({
  id,
  controls,
  active,
  onClick,
  children,
}: {
  id: string;
  controls: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-controls={controls}
      aria-selected={active}
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      className={cn(
        'flex-1 rounded-full px-4 py-2 text-[13px] font-semibold transition',
        active
          ? 'bg-or-orange-500 text-white shadow-or-card'
          : 'text-or-orange-700 hover:text-or-orange-800',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Single-resource card for the per-book vocabulary bookmark, rendered
 * inside the Resources tab when `book.vocabularyBookmark` is set. Shows
 * the image as a thumbnail (parents preview before printing) plus
 * Open / Download CTAs. The Download anchor uses the `download` attr
 * with the book title in the filename so it lands cleanly in the user's
 * downloads folder rather than as `<hash>.png`.
 *
 * Both the wrapping `<PaywalledSection>` and our internal `download`
 * fetch are independent: free readers never reach the click because
 * PaywalledSection captures clicks at the overlay layer.
 */
function VocabularyBookmarkCard({ url, bookTitle }: { url: string; bookTitle: string }) {
  // Derive a friendly download filename from the book title — extension
  // is taken from the URL so the download matches the actual stored
  // bytes (png / jpg / webp / gif).
  const ext = url.split('.').pop()?.split('?')[0] ?? 'png';
  const safeTitle =
    bookTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'vocabulary-bookmark';
  const downloadName = `${safeTitle}-vocabulary-bookmark.${ext}`;

  return (
    <div className="mb-6 rounded-2xl border border-or-orange-100 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-or-orange-100 text-or-orange-700"
        >
          <Sparkles className="h-3.5 w-3.5" />
        </span>
        <div>
          <div className="or-kicker text-or-orange-700">Book resource</div>
          <h3 className="font-display text-[16px] font-bold text-or-slate-900">
            Vocabulary bookmark
          </h3>
        </div>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-or-slate-600">
        A printable bookmark with the key vocabulary from this book — slip it next to your
        reader while they read.
      </p>
      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="block shrink-0"
          aria-label="Open vocabulary bookmark in a new tab"
        >
          <img
            src={url}
            alt={`${bookTitle} vocabulary bookmark`}
            width={120}
            height={170}
            className="h-[170px] w-[120px] rounded-xl border border-or-slate-100 object-contain shadow-or-card"
          />
        </a>
        <div className="flex flex-1 flex-wrap gap-2">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full border border-or-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-or-slate-700 hover:bg-or-slate-50"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            Open
          </a>
          <a
            href={url}
            download={downloadName}
            className="inline-flex items-center gap-1.5 rounded-full bg-or-orange-500 px-3.5 py-2 text-[13px] font-semibold text-white shadow-or-card hover:bg-or-orange-600"
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            Download
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Multi-row card for the per-book downloadable activity PDFs in the
 * Resources tab. Each row carries the title + Open / Download buttons,
 * mirroring the vocab-bookmark card's affordances. PaywalledSection
 * wraps the whole card so free readers see the contents but every
 * interaction routes to the upgrade modal.
 *
 * Filename hygiene: the `download` attr on the anchor uses the title
 * sanitized to kebab-case so the file lands in the user's downloads
 * folder as something readable rather than `<sha256>.pdf`.
 */
function ActivitiesCard({
  activities,
  bookTitle,
}: {
  activities: BookActivityView[];
  bookTitle: string;
}) {
  const safeBookTitle =
    bookTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'book';

  return (
    <div className="mb-6 rounded-2xl border border-or-orange-100 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-or-orange-100 text-or-orange-700"
        >
          <FileText className="h-3.5 w-3.5" />
        </span>
        <div>
          <div className="or-kicker text-or-orange-700">Book resource</div>
          <h3 className="font-display text-[16px] font-bold text-or-slate-900">Activities</h3>
        </div>
      </div>
      <p className="mt-2 text-[13px] leading-relaxed text-or-slate-600">
        {activities.length === 1
          ? 'A printable PDF to extend the book — perfect alongside reading.'
          : `${activities.length} printable PDFs to extend the book — perfect alongside reading.`}
      </p>
      <ul className="mt-4 flex flex-col divide-y divide-or-orange-100">
        {activities.map((activity) => {
          const safeTitle =
            activity.title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '')
              .slice(0, 60) || 'activity';
          const downloadName = `${safeBookTitle}-${safeTitle}.pdf`;
          return (
            <li
              key={activity.id}
              className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-start gap-3">
                <span
                  aria-hidden
                  className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-or-cream-50 text-or-orange-600"
                >
                  <FileText className="h-4 w-4" />
                </span>
                <span className="break-words text-[14px] font-semibold text-or-slate-900">
                  {activity.title}
                </span>
              </div>
              <div className="flex shrink-0 flex-wrap gap-2 sm:ml-4">
                <a
                  href={activity.fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-or-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-or-slate-700 hover:bg-or-slate-50"
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                  Open
                </a>
                <a
                  href={activity.fileUrl}
                  download={downloadName}
                  className="inline-flex items-center gap-1.5 rounded-full bg-or-orange-500 px-3 py-1.5 text-[12px] font-semibold text-white shadow-or-card hover:bg-or-orange-600"
                >
                  <Download className="h-3.5 w-3.5" aria-hidden />
                  Download
                </a>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Quiet loading shell shown while the activities list is in flight. Same
 * outer frame as the real card so the layout doesn't jump when the data
 * arrives. Renders nothing when the response is empty (the card is
 * conditionally hidden upstream).
 */
function ActivitiesCardSkeleton() {
  return (
    <div className="mb-6 animate-pulse rounded-2xl border border-or-slate-100 bg-white p-5">
      <div className="h-4 w-32 rounded-full bg-or-slate-100" />
      <div className="mt-3 h-3 w-3/4 rounded-full bg-or-slate-100" />
      <div className="mt-4 h-10 rounded-lg bg-or-slate-50" />
    </div>
  );
}

function LabelLine({ label, labels }: { label: string; labels: BookLabelView[] }) {
  if (labels.length === 0) return null;
  return (
    <div className="text-[13px] text-or-slate-700">
      <span className="font-semibold text-or-slate-900">{label}: </span>
      {labels.map((l) => l.name).join(', ')}
    </div>
  );
}

function LabelChipGroup({ label, labels }: { label: string; labels: BookLabelView[] }) {
  return (
    <div>
      <div className="or-kicker mb-1.5">{label}</div>
      {labels.length === 0 ? (
        <div className="text-[13px] text-or-slate-500">Not set.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {labels.map((l) => (
            <span
              key={l.id}
              className="inline-flex items-center rounded-full border border-or-slate-200 bg-white px-2.5 py-0.5 text-[12px] text-or-slate-700"
            >
              {l.name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
